import React, { useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, ZoomControl } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { BarChart3, MapPin } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCustomersMap, type CustomerMapPoint } from '@/hooks/useCustomersMap';
import {
  buildPieIcon, euroShort, STATUS_COLOR, STATUS_LABEL, STATUS_ORDER,
  type StatusKey,
} from '@/utils/map/pieIcon';

/**
 * Mappa inline per la pagina /clienti.
 *
 * Ogni pin cliente è una mini-pie chart proporzionale agli € dei suoi
 * preventivi suddivisi per stato (draft/sent/won/lost). I cluster aggregano
 * gli stessi 4 bucket sui clienti raggruppati per zoom. I 4 checkbox in alto
 * controllano quali stati sono visibili — filtro applicato sia ai pin che,
 * via prop controlled, alla lista clienti del parent.
 *
 * Pattern: split list+map alla Airbnb/Booking. Usato da Customers.tsx su ≥1280px.
 */
export type StatusFilter = Record<StatusKey, boolean>;

export const DEFAULT_STATUS_FILTER: StatusFilter = {
  draft: true, sent: true, won: true, lost: true,
};

interface CustomersMapInlineProps {
  /** Set di ID visibili nella lista (passato per filtro coerente con la search). */
  visibleIds: Set<string>;
  selectedId?: string | null;
  onSelect?: (customerId: string) => void;
  height?: string;
  /** Controlled: se passato, lo state filtro stati vive nel parent (per filtrare anche la lista). */
  statusFilter?: StatusFilter;
  onStatusFilterChange?: (next: StatusFilter) => void;
}

const ITALY_CENTER: [number, number] = [42.5, 12.5];

function euroFull(v: number): string {
  if (!Number.isFinite(v) || v === 0) return '€ 0';
  return v.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  });
}

/**
 * Somma gli amount visibili (filtrati per statusFilter) di un cliente.
 * Usata per dimensionare il pin proporzionalmente agli € rilevanti.
 */
function visibleTotal(c: CustomerMapPoint, filter: StatusFilter): number {
  let sum = 0;
  for (const k of STATUS_ORDER) {
    if (filter[k]) sum += c.byStatus[k].amount;
  }
  return sum;
}

/**
 * Raggio in [12, 32]px proporzionale alla radice quadrata di amount/max
 * (area visiva proporzionale all'€).
 */
function radiusForAmount(amount: number, maxAmount: number): number {
  if (maxAmount <= 0 || amount <= 0) return 12;
  const ratio = Math.sqrt(Math.min(1, amount / maxAmount));
  return Math.round(12 + ratio * 20);
}

const MapFocus: React.FC<{ point: CustomerMapPoint | null }> = ({ point }) => {
  const map = useMap();
  useEffect(() => {
    if (!point || point.latitude == null || point.longitude == null) return;
    map.flyTo([point.latitude, point.longitude], Math.max(map.getZoom(), 13), {
      duration: 0.6,
    });
  }, [point, map]);
  return null;
};

const InitialFit: React.FC<{ points: CustomerMapPoint[] }> = ({ points }) => {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    const withCoords = points.filter(p => p.latitude != null && p.longitude != null);
    if (withCoords.length === 0) return;
    if (withCoords.length === 1) {
      const c = withCoords[0];
      map.setView([c.latitude as number, c.longitude as number], 12);
    } else {
      const bounds = L.latLngBounds(
        withCoords.map(c => [c.latitude as number, c.longitude as number]),
      );
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    }
    done.current = true;
  }, [points, map]);
  return null;
};

const CustomersMapInline: React.FC<CustomersMapInlineProps> = ({
  visibleIds,
  selectedId,
  onSelect,
  height = 'calc(100vh - 12rem)',
  statusFilter: statusFilterProp,
  onStatusFilterChange,
}) => {
  const navigate = useNavigate();
  // Controlled se il parent passa filter+onChange, altrimenti state interno
  const [filterInternal, setFilterInternal] = React.useState<StatusFilter>(DEFAULT_STATUS_FILTER);
  const filter = statusFilterProp ?? filterInternal;
  const setFilter = (next: StatusFilter) => {
    if (onStatusFilterChange) onStatusFilterChange(next);
    else setFilterInternal(next);
  };
  const toggleStatus = (key: StatusKey) => setFilter({ ...filter, [key]: !filter[key] });

  const { data: points = [], isLoading } = useCustomersMap();

  // Filtra coerentemente con la lista (search applicata via visibleIds)
  const visiblePoints = useMemo(
    () => points.filter(p => visibleIds.has(p.id)),
    [points, visibleIds],
  );
  const withCoords = useMemo(
    () => visiblePoints.filter(p => p.latitude != null && p.longitude != null),
    [visiblePoints],
  );
  const withoutCoords = visiblePoints.length - withCoords.length;

  // Filtra per stati attivi: un cliente è visibile se ha almeno un € in uno
  // degli stati attivi (oppure nessuno stato è attivo → mostro tutti, fallback)
  const allStatusOff = !filter.draft && !filter.sent && !filter.won && !filter.lost;
  const visibleMarkers = useMemo(() => {
    if (allStatusOff) return [];
    return withCoords.filter(c => visibleTotal(c, filter) > 0);
  }, [withCoords, filter, allStatusOff]);

  const maxAmount = useMemo(
    () => visibleMarkers.reduce((acc, c) => Math.max(acc, visibleTotal(c, filter)), 0),
    [visibleMarkers, filter],
  );

  // Totale aggregato degli stati selezionati (mostrato nel footer)
  const totalSelected = useMemo(
    () => visibleMarkers.reduce((sum, c) => sum + visibleTotal(c, filter), 0),
    [visibleMarkers, filter],
  );

  const selectedPoint = useMemo(
    () => (selectedId ? withCoords.find(p => p.id === selectedId) ?? null : null),
    [selectedId, withCoords],
  );

  // Cluster icon: SVG pie con il breakdown aggregato dei child marker.
  // Filtro stati applicato anche all'aggregazione.
  const buildClusterIcon = useMemo(() => {
    return (cluster: L.MarkerCluster): L.DivIcon => {
      const childMarkers = cluster.getAllChildMarkers();
      const agg: Record<StatusKey, number> = { draft: 0, sent: 0, won: 0, lost: 0 };
      let totalCount = 0;
      for (const m of childMarkers) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = (m.options as any).statusAmounts as Record<StatusKey, number> | undefined;
        if (!data) continue;
        for (const k of STATUS_ORDER) {
          if (filter[k]) agg[k] += data[k];
        }
        totalCount += 1;
      }
      const total = agg.draft + agg.sent + agg.won + agg.lost;
      // Diametro cluster scala log con il count
      const r = Math.min(36, 18 + Math.log2(totalCount + 1) * 4);
      return buildPieIcon({
        radius: r,
        slices: STATUS_ORDER.map(s => ({ status: s, amount: agg[s] })),
        centerLabel: String(totalCount),
        centerSubLabel: total > 0 ? euroShort(total) : undefined,
      });
    };
  }, [filter]);

  return (
    <TooltipProvider delayDuration={250}>
      <div className="relative rounded-md border bg-white overflow-hidden" style={{ height }}>
        {/* Header overlay: 4 checkbox stati + bottone analisi avanzata */}
        <div className="absolute top-3 left-3 right-3 z-[1000] flex items-start justify-between gap-2 pointer-events-none">
          <div className="bg-white/95 backdrop-blur-sm rounded-md shadow-sm border pointer-events-auto px-2 py-1.5">
            <div className="flex items-center gap-3 flex-wrap text-xs">
              {STATUS_ORDER.map(s => (
                <label
                  key={s}
                  className="flex items-center gap-1.5 cursor-pointer select-none"
                >
                  <Checkbox
                    checked={filter[s]}
                    onCheckedChange={() => toggleStatus(s)}
                    className="h-3.5 w-3.5"
                  />
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full border border-gray-300"
                    style={{ background: STATUS_COLOR[s] }}
                  />
                  <span className="text-foreground">{STATUS_LABEL[s]}</span>
                </label>
              ))}
            </div>
          </div>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate('/customers/map')}
                className="shadow-md gap-1.5 pointer-events-auto"
              >
                <BarChart3 className="h-3.5 w-3.5" />
                Analisi avanzata
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-[260px]">
              <p className="text-xs">
                Apri la mappa a tutta pagina con <strong>strumenti di disegno</strong> (cerchi,
                rettangoli, poligoni) per delimitare zone operative e ottenere <strong>KPI di
                zona</strong>: clienti, fatturato vinto, conversion rate, top 3.
              </p>
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Footer overlay: stats compatte */}
        {visiblePoints.length > 0 && (
          <div className="absolute bottom-2 left-2 right-14 z-[1000] text-[11px] bg-white/90 backdrop-blur-sm px-2 py-1.5 rounded-md flex items-center justify-between gap-2 pointer-events-none">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <MapPin className="h-3 w-3" />
              {visibleMarkers.length}/{visiblePoints.length} su mappa
              {withoutCoords > 0 && (
                <span className="text-amber-700">· {withoutCoords} senza coordinate</span>
              )}
            </span>
            {totalSelected > 0 && (
              <span className="text-foreground font-medium">
                Totale: {euroFull(totalSelected)}
              </span>
            )}
          </div>
        )}

        {isLoading && (
          <div className="absolute inset-0 z-[500] flex items-center justify-center bg-white/50 text-xs text-muted-foreground">
            Caricamento metriche…
          </div>
        )}

        <MapContainer
          center={ITALY_CENTER}
          zoom={6}
          scrollWheelZoom
          zoomControl={false}
          style={{ width: '100%', height: '100%' }}
        >
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {/* Zoom in basso-dx per non sovrapporsi al filtro stati in alto-sx */}
          <ZoomControl position="bottomright" />
          <InitialFit points={withCoords} />
          <MapFocus point={selectedPoint} />
          <MarkerClusterGroup
            // key cambia ad ogni filtro → forza remount cluster, altrimenti
            // react-leaflet-cluster non aggiorna i child rimossi/aggiunti
            key={Object.entries(filter).map(([k, v]) => `${k}:${v ? 1 : 0}`).join('|')}
            chunkedLoading
            showCoverageOnHover={false}
            maxClusterRadius={60}
            spiderfyOnMaxZoom
            iconCreateFunction={buildClusterIcon}
          >
            {visibleMarkers.map(c => {
              const total = visibleTotal(c, filter);
              const radius = radiusForAmount(total, maxAmount);
              const isSelected = c.id === selectedId;
              const slices = STATUS_ORDER.map(s => ({
                status: s,
                amount: filter[s] ? c.byStatus[s].amount : 0,
              }));
              const statusAmounts: Record<StatusKey, number> = {
                draft: filter.draft ? c.byStatus.draft.amount : 0,
                sent:  filter.sent  ? c.byStatus.sent.amount  : 0,
                won:   filter.won   ? c.byStatus.won.amount   : 0,
                lost:  filter.lost  ? c.byStatus.lost.amount  : 0,
              };
              return (
                <Marker
                  // Key composta su filtri+id forza ri-creazione marker
                  // quando cambia filtro (l'icona dipende da filter)
                  key={`${Object.values(filter).join('')}-${c.id}`}
                  position={[c.latitude as number, c.longitude as number]}
                  icon={buildPieIcon({
                    radius,
                    slices,
                    highlighted: isSelected,
                  })}
                  eventHandlers={{
                    click: () => onSelect?.(c.id),
                  }}
                  // Custom prop letto da buildClusterIcon per aggregare
                  // gli stati nel cluster. Leaflet passa le options al marker.
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  {...({ statusAmounts } as any)}
                >
                  <Popup>
                    <div className="space-y-1 min-w-[220px]">
                      <p className="font-semibold text-sm">{c.name}</p>
                      {c.city && (
                        <p className="text-xs text-muted-foreground">
                          {[c.city, c.province].filter(Boolean).join(' (')}
                          {c.province ? ')' : ''}
                        </p>
                      )}
                      <div className="text-xs space-y-0.5 pt-1 border-t mt-1">
                        <p>Preventivi: <strong>{c.estimatesCount}</strong></p>
                        {STATUS_ORDER.map(s => (
                          c.byStatus[s].count > 0 && (
                            <p key={s} className="flex items-center gap-1.5">
                              <span
                                className="inline-block w-2 h-2 rounded-full border border-gray-300"
                                style={{ background: STATUS_COLOR[s] }}
                              />
                              <span>{STATUS_LABEL[s]}:</span>
                              <strong>{euroFull(c.byStatus[s].amount)}</strong>
                              <span className="text-muted-foreground">({c.byStatus[s].count})</span>
                            </p>
                          )
                        ))}
                        <p className="pt-1 border-t border-dashed">
                          Totale: <strong>{euroFull(c.totalAmount)}</strong>
                        </p>
                      </div>
                      <Link
                        to={`/customers/${c.id}`}
                        className="text-xs text-primary underline pt-1 inline-block"
                      >
                        Apri scheda →
                      </Link>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MarkerClusterGroup>
        </MapContainer>
      </div>
    </TooltipProvider>
  );
};

export default CustomersMapInline;
